import DOMPurify from 'dompurify';

/**
 * Sanitizer for CMS-authored HTML (theory `html-content` blocks and step
 * instructions).
 *
 * Courses rely on third-party embeds (videos, games, external tools), so
 * `<iframe>` is explicitly allowed — DOMPurify's defaults would strip it.
 * What stays forbidden, deliberately:
 * - `<script>`, event handlers, and the rest of DOMPurify's defaults.
 * - `srcdoc`: an iframe with inline HTML runs in the app's own origin — a
 *   script inside it would execute as the app (XSS), unlike a cross-origin
 *   `src` embed which the browser isolates.
 * - `javascript:` URLs in `src` are already rejected by DOMPurify's URI check.
 *
 * `<oembed url>` (CKEditor's media-embed output) is kept only so
 * `prepareCmsHtml` can replace it — it is never rendered as-is.
 */
export function sanitizeCmsHtml(html: string | null | undefined): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ADD_TAGS: ['iframe', 'oembed'],
    ADD_ATTR: [
      'url',
      'allow',
      'allowfullscreen',
      'frameborder',
      'scrolling',
      'referrerpolicy',
      'loading',
    ],
    FORBID_ATTR: ['srcdoc'],
  });
}

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
]);
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_PATH_ID = /^\/(?:shorts|embed|live)\/([A-Za-z0-9_-]{11})\/?$/;
const YOUTUBE_SHORT_PATH_ID = /^\/([A-Za-z0-9_-]{11})\/?$/;
/** `t`/`start` values: `90`, `90s`, `1m30s`, `1h2m3s`. */
const YOUTUBE_TIME = /^(?:(\d{1,2})h)?(?:(\d{1,4})m)?(?:(\d{1,6})s?)?$/;
const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com']);
/** `/ID` or `/ID/HASH` (unlisted videos carry a privacy hash). */
const VIMEO_PATH = /^\/(\d{1,12})(?:\/([0-9a-f]{6,20}))?\/?$/;

function youtubeStartSeconds(url: URL): number {
  const raw = url.searchParams.get('t') ?? url.searchParams.get('start');
  const match = raw ? YOUTUBE_TIME.exec(raw) : null;
  if (!match) return 0;
  const [, h = '0', m = '0', s = '0'] = match;
  return Number(h) * 3600 + Number(m) * 60 + Number(s);
}

/**
 * Maps a CKEditor media-embed URL to a player URL for an allow-listed
 * provider, or null. The iframe src is always rebuilt from a strictly matched
 * video ID — never passed through from the author's URL.
 */
function oembedPlayerSrc(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

  let youtubeId: string | undefined;
  if (url.hostname === 'youtu.be') {
    youtubeId = YOUTUBE_SHORT_PATH_ID.exec(url.pathname)?.[1];
  } else if (YOUTUBE_HOSTS.has(url.hostname)) {
    const v = url.searchParams.get('v');
    youtubeId =
      url.pathname === '/watch' && v && YOUTUBE_ID.test(v)
        ? v
        : YOUTUBE_PATH_ID.exec(url.pathname)?.[1];
  }
  if (youtubeId) {
    const start = youtubeStartSeconds(url);
    return `https://www.youtube-nocookie.com/embed/${youtubeId}${start > 0 ? `?start=${start}` : ''}`;
  }

  if (VIMEO_HOSTS.has(url.hostname)) {
    const match = VIMEO_PATH.exec(url.pathname);
    if (match) {
      const [, id, hash] = match;
      return `https://player.vimeo.com/video/${id}${hash ? `?h=${hash}` : ''}`;
    }
  }
  return null;
}

/**
 * Replaces each CKEditor `<oembed url>` with a player iframe (known provider)
 * or a plain link (unknown provider) — the browser renders `<oembed>` as
 * nothing, which left media embeds as empty space. Anything that isn't an
 * http(s) URL is dropped.
 */
function replaceOembeds(doc: Document): void {
  doc.querySelectorAll('oembed').forEach((oembed) => {
    const raw = oembed.getAttribute('url')?.trim() ?? '';
    const src = oembedPlayerSrc(raw);
    if (src) {
      const frame = doc.createElement('iframe');
      frame.setAttribute('src', src);
      frame.setAttribute('title', 'Video');
      frame.setAttribute(
        'allow',
        'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture; web-share',
      );
      frame.setAttribute('allowfullscreen', '');
      // YouTube refuses to play embeds that send no referrer.
      frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      frame.setAttribute('loading', 'lazy');
      oembed.replaceWith(frame);
      return;
    }
    let href: URL | null = null;
    try {
      href = new URL(raw);
    } catch {
      // not a URL — dropped below
    }
    if (href && (href.protocol === 'https:' || href.protocol === 'http:')) {
      const link = doc.createElement('a');
      link.setAttribute('href', href.href);
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      link.textContent = href.href;
      oembed.replaceWith(link);
    } else {
      oembed.remove();
    }
  });
}

/**
 * Viewport share an embed may occupy vertically. Chosen so a wide video fits
 * fully on screen under the app header without scrolling.
 */
const EMBED_MAX_VIEWPORT_HEIGHT = '70vh';

/**
 * Sanitizes CMS HTML and makes embeds fit the screen: CMS editors save
 * iframes with fixed `width`/`height` attributes (e.g. 640×360), which ignore
 * the actual content column. Each iframe keeps its own declared aspect ratio
 * (YouTube 16:9, Drive preview 4:3, …) and fills the available width — unless
 * that would make it taller than the viewport, in which case it is sized to
 * fit the height cap and centered, so the whole embed is visible without
 * scrolling. CKEditor media embeds (`<oembed>`) become iframes first, so they
 * get the same sizing (16:9). Runs AFTER sanitization — the styles and embed
 * markup are ours, not the author's.
 */
export function prepareCmsHtml(html: string | null | undefined): string {
  const sanitized = sanitizeCmsHtml(html);
  const lower = sanitized.toLowerCase();
  if (!lower.includes('<iframe') && !lower.includes('<oembed'))
    return sanitized;

  const doc = new DOMParser().parseFromString(sanitized, 'text/html');
  replaceOembeds(doc);
  doc.querySelectorAll('iframe').forEach((frame) => {
    const width = Number(frame.getAttribute('width'));
    const height = Number(frame.getAttribute('height'));
    const hasRatio = width > 0 && height > 0;
    const ratio = hasRatio ? width / height : 16 / 9;
    // Set via the style attribute: jsdom's CSSOM (used in unit tests) drops
    // min()/calc() values assigned through frame.style properties.
    frame.setAttribute(
      'style',
      [
        `aspect-ratio: ${hasRatio ? `${width} / ${height}` : '16 / 9'}`,
        `width: min(100%, calc(${EMBED_MAX_VIEWPORT_HEIGHT} * ${ratio.toFixed(4)}))`,
        'height: auto',
        'border: 0',
        'display: block',
        'margin: 0 auto',
      ].join('; '),
    );
  });
  return doc.body.innerHTML;
}
